import { useEffect, useState } from "react";
import { Navbar } from "@/components/Navbar";
import { PageBackground } from "@/components/PageBackground";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Users, ShieldCheck, FileText, CreditCard, Gift } from "lucide-react";

const StatCard = ({ icon: Icon, label, value }) => (
  <div className="bg-white border border-border rounded-sm p-5">
    <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1"><Icon className="w-4 h-4" />{label}</div>
    <div className="font-display text-3xl font-bold">{value}</div>
  </div>
);

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [verifs, setVerifs] = useState([]);
  const [payments, setPayments] = useState([]);
  const [promo, setPromo] = useState(null);
  const [promoLimit, setPromoLimit] = useState(100);

  const load = async () => {
    const [s, u, v, p, pr] = await Promise.all([
      api.get("/admin/stats"), api.get("/admin/users"), api.get("/admin/verifications"), api.get("/admin/payments"), api.get("/admin/promo"),
    ]);
    setStats(s.data); setUsers(u.data); setVerifs(v.data); setPayments(p.data);
    setPromo(pr.data); setPromoLimit(pr.data.limit);
  };
  useEffect(() => { load(); }, []);

  const approve = async (uid) => { await api.post(`/admin/verifications/${uid}/approve`); toast.success("Tenant verified"); load(); };

  const savePromo = async (patch) => {
    try {
      const { data } = await api.post("/admin/promo", patch);
      setPromo(data); setPromoLimit(data.limit);
      toast.success("Promo updated");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update promo"); }
  };

  if (!stats) return <div className="min-h-screen grid place-items-center text-muted-foreground">Loading…</div>;

  return (
    <div>
      <PageBackground variant={0} />
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 md:px-8 py-8" data-testid="admin-dashboard">
        <h1 className="font-display text-3xl font-semibold tracking-tight mb-6">Admin</h1>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard icon={Users} label="Users" value={stats.users} />
          <StatCard icon={ShieldCheck} label="Verified tenants" value={stats.verified_tenants} />
          <StatCard icon={FileText} label="Offers" value={stats.offers} />
          <StatCard icon={CreditCard} label="Paid txns" value={stats.payments} />
        </div>

        {/* First-100 free promo control */}
        {promo && (
          <div className="bg-white border border-border rounded-sm p-5 mb-8" data-testid="promo-control">
            <div className="flex items-center gap-2 mb-3">
              <Gift className="w-5 h-5 text-primary" />
              <span className="font-display text-lg font-semibold">Free verification promo</span>
              <span className="text-sm text-muted-foreground" data-testid="promo-summary">
                {promo.used} used · {promo.enabled ? `${promo.spots_left} spots left` : "disabled"}
              </span>
            </div>
            <div className="flex flex-wrap items-end gap-4">
              <div className="flex items-center gap-2">
                <Switch data-testid="promo-enabled" checked={promo.enabled} onCheckedChange={(v) => savePromo({ enabled: v })} />
                <Label className="text-sm">{promo.enabled ? "Active" : "Paused"}</Label>
              </div>
              <div>
                <Label className="text-xs">Free tenant limit</Label>
                <Input data-testid="promo-limit" type="number" min="0" className="w-32" value={promoLimit} onChange={(e) => setPromoLimit(e.target.value)} />
              </div>
              <Button data-testid="promo-save" onClick={() => savePromo({ limit: Number(promoLimit), enabled: true })}>
                Extend / relaunch
              </Button>
            </div>
          </div>
        )}

        <Tabs defaultValue="verifs">
          <TabsList className="mb-4">
            <TabsTrigger value="verifs" data-testid="admin-tab-verifs">Verifications</TabsTrigger>
            <TabsTrigger value="users" data-testid="admin-tab-users">Users</TabsTrigger>
            <TabsTrigger value="payments" data-testid="admin-tab-payments">Payments</TabsTrigger>
          </TabsList>

          <TabsContent value="verifs">
            <div className="bg-white border border-border rounded-sm divide-y divide-border">
              {verifs.map((v) => (
                <div key={v.user_id} className="p-4 flex items-center justify-between">
                  <div>
                    <div className="font-semibold">{v.name} <span className="text-xs text-muted-foreground">{v.email}</span></div>
                    <div className="text-xs text-muted-foreground">Status: {v.verification_status} · Matchmaking: {v.matchmaking_status} {v.credit_score ? `· Credit ${v.credit_score}` : ""}</div>
                  </div>
                  {v.verified ? <span className="text-xs text-emerald-700 font-semibold">Verified</span> :
                    <Button size="sm" variant="outline" data-testid={`approve-${v.user_id}`} onClick={() => approve(v.user_id)}>Manually verify</Button>}
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="users">
            <div className="bg-white border border-border rounded-sm divide-y divide-border">
              {users.map((u) => (
                <div key={u.id} className="p-4 flex items-center justify-between text-sm">
                  <div><span className="font-semibold">{u.name}</span> · {u.email}</div>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100">{u.role}</span>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="payments">
            <div className="bg-white border border-border rounded-sm divide-y divide-border">
              {payments.length === 0 && <div className="p-6 text-sm text-muted-foreground">No payments yet.</div>}
              {payments.map((p) => (
                <div key={p.session_id} className="p-4 flex items-center justify-between text-sm">
                  <div>{p.email} · <span className="text-muted-foreground">{p.payment_type}</span></div>
                  <div className="flex items-center gap-3">
                    <span>${p.amount}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${p.payment_status === "paid" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100"}`}>{p.payment_status}</span>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
